import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
	SpotlightStepCount,
} from '@atlaskit/spotlight';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import {
	useGettingStartedTourState,
	useGettingStartedTourStoreActions,
} from '../../controllers/getting-started-tour';

import { i18n } from './messages';

type GettingStartedTourSpotlightProps = {
	/**
	 * entry point for the tour to render. Currently only entry points ending with {@link ALLOWED_ENTRY_POINT} will render
	 **/
	entryPoint?: string;
	/** This is not the current step, but the step that the children should render **/
	step: 1 | 2 | 3;
	children?: any;
};

const stepConfig = [
	{
		title: i18n.stepOneTitle,
		description: i18n.stepOneDescription,
		placement: 'bottom-start',
	},
	{
		title: i18n.stepTwoTitle,
		description: i18n.stepTwoDescription,
		placement: 'left-start',
	},
	{
		title: i18n.stepThreeTitle,
		description: i18n.stepThreeDescription,
		placement: 'bottom-end',
	},
] as const;

const ALLOWED_ENTRY_POINT = '-smartCreate';

export const GettingStartedTourSpotlight = ({
	entryPoint,
	children,
	step,
}: GettingStartedTourSpotlightProps): any => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { stepNumber: currentStepNumber, totalSteps } = useGettingStartedTourState();
	const {
		setStepNumber: hideGettingStartedSpotlightTour,
		nextStep: moveToNextStep,
		previousStep: moveToPreviousStep,
		dismiss: completeGettingStartedTour,
	} = useGettingStartedTourStoreActions();

	const handleOnNextClicked = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'button',
			action: 'clicked',
			actionSubjectId: 'gettingStartedTourSpotlightPrimaryButton',
			attributes: {
				tourStep: step,
			},
			source: 'smart-create',
		});

		if (step === totalSteps) {
			completeGettingStartedTour();
		} else {
			moveToNextStep();
		}
	}, [step, totalSteps, completeGettingStartedTour, moveToNextStep, sendAnalyticsEvent]);

	const handleOnPreviousClicked = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'button',
			action: 'clicked',
			actionSubjectId: 'gettingStartedTourSpotlightSecondaryButton',
			attributes: {
				tourStep: step,
			},
			source: 'smart-create',
		});

		moveToPreviousStep();
	}, [step, moveToPreviousStep, sendAnalyticsEvent]);

	const handleOnDismissClicked = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'button',
			action: 'clicked',
			actionSubjectId: 'gettingStartedTourSpotlightDismissButton',
			attributes: {
				tourStep: step,
			},
			source: 'smart-create',
		});

		hideGettingStartedSpotlightTour(0);
	}, [step, hideGettingStartedSpotlightTour, sendAnalyticsEvent]);

	if (
		!fg('platform_aifc_getting_started_spotlight') ||
		!entryPoint?.includes(ALLOWED_ENTRY_POINT)
	) {
		return children;
	}

	const currentConfig = stepConfig[step - 1];

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				dismiss={handleOnDismissClicked}
				isVisible={currentStepNumber === step}
				placement={currentConfig?.placement}
				strategy="absolute"
			>
				{(() => {
					const spotlightCard = (
						<SpotlightCard testId="spotlight">
							<SpotlightHeader>
								<SpotlightHeadline>{formatMessage(currentConfig?.title)}</SpotlightHeadline>
								<SpotlightControls>
									<SpotlightDismissControl onClick={handleOnDismissClicked} />
								</SpotlightControls>
							</SpotlightHeader>
							<SpotlightBody>{formatMessage(currentConfig?.description)}</SpotlightBody>
							<SpotlightFooter>
								<SpotlightStepCount>
									{formatMessage(i18n.stepIndicator, {
										stepNumber: step,
										totalSteps,
									})}
								</SpotlightStepCount>
								<SpotlightActions>
									{currentStepNumber > 1 && (
										<SpotlightSecondaryAction onClick={handleOnPreviousClicked}>
											{formatMessage(i18n.back)}
										</SpotlightSecondaryAction>
									)}
									<SpotlightPrimaryAction onClick={handleOnNextClicked}>
										{currentStepNumber < totalSteps
											? formatMessage(i18n.next)
											: formatMessage(i18n.done)}
									</SpotlightPrimaryAction>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					);
					return fg('platform-dst-motion-uplift-spotlight') ? (
						spotlightCard
					) : (
						<FadeIn entranceDirection="left">
							{(props) => <div {...props}>{spotlightCard}</div>}
						</FadeIn>
					);
				})()}
			</PopoverContent>
		</PopoverProvider>
	);
};
